import styled from "styled-components";

// NOTE ON THE POST-SKELETON HAND-OFF:
//
// Earlier iterations (PR #60) applied a one-shot fade-in to the real
// chrome. That approach sat on top of an early-return gate that
// *replaced* the real chrome with the shell skeleton during loading —
// which turned out to silently block the bootstrap `fetchMatterDetail`
// dispatch that lives inside `TabView`. See the `isInitialLoad` comment
// in `MatterDetails.js` for the full history.
//
// Today the real chrome stays mounted throughout the loading window and
// the `ShellSkeletonOverlay` below fades OUT on top of it. No animation
// needs to live on `MatterDetailsWrapper` itself — which is important
// because this component also interpolates `aiPaneOpen`, so any
// prop-driven animation branch here would restart every time the AI
// panel toggles. Do not re-add a prop-driven `animation` branch to
// `MatterDetailsWrapper` without first rearchitecting the
// `aiPaneOpen`-dependent CSS out of this component.
//
// `aiPaneOpen` is the successor to the older `chatboxOpen` prop. Both
// props are still accepted at the boundary so older tests / consumers
// don't break during the cutover; the styled rules read `aiPaneOpen`
// first and fall back to `chatboxOpen`.

const isPaneOpen = (props) => Boolean(props.aiPaneOpen ?? props.chatboxOpen);

export const MatterDetailsWrapper = styled.div`
  padding: 10px 0px 0px 0px;
  display: flex;
  width: 100%;
  max-width: 100vw; /* Prevent exceeding viewport width */
  height: 100%;
  overflow: hidden;
  overflow-x: hidden; /* Explicitly prevent horizontal scroll */
  box-sizing: border-box;
  position: relative;
  
  /* Ensure all children respect the viewport constraint */
  > * {
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* When the AI pane is open, ensure total width doesn't exceed viewport */
  /* This prevents the panel from being cut off */
  ${(props) => isPaneOpen(props) && `
    max-width: 100vw;
    overflow-x: hidden;
  `}
  
  /* Ensure flex children shrink properly when the AI pane is open */
  ${(props) => isPaneOpen(props) && `
    > div:first-of-type {
      flex-shrink: 1;
      min-width: 0;
    }
  `}
`;

export const MainContentWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  max-width: 100%; /* Prevent exceeding parent width */
  transition: flex 0.3s ease;
  flex-shrink: 1; /* Always allow shrinking when sidebar widens or AI pane opens */
  overflow: hidden; /* Prevent overflow */
  overflow-x: hidden; /* Explicitly prevent horizontal scroll */
  box-sizing: border-box;
  
  /* When the AI pane is open, ensure we don't exceed available space */
  /* The parent MatterDetailsWrapper already constrains to 100vw, so we use 100% relative to it */
  ${(props) => isPaneOpen(props) && `
    max-width: 100%; /* Let flexbox handle the shrinking, parent already constrains to viewport */
    flex-shrink: 1; /* Ensure it shrinks to make room for the AI panel */
  `}
  
  /* Ensure all children respect parent width */
  > * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

/**
 * Right-rail slot for the matter-workspace AI panel (Briefing / Analysis /
 * Chat). Historically this was named `ChatPaneWrapper` because it housed
 * just the chat surface; the export is kept under both names so older
 * consumers keep importing the wrapper they used to. Width and slide
 * behavior are unchanged from the legacy chat surface so existing layout
 * code in `MatterDetails` doesn't need to be re-tuned.
 */
export const AIPaneWrapper = styled.div`
  width: ${(props) => props.$width || "500px"};
  min-width: ${(props) => props.$width || "500px"};
  max-width: ${(props) => props.$width || "500px"};
  /*
   * Bind the pane height to its PARENT (MatterDetailsWrapper, which is
   * height:100% of body { height: 95vh }), NOT to the viewport. The
   * old calc(100vh - 20px) was ~98vh — taller than the 95vh body that
   * contains it — so the pane's bottom slice rendered below the fold
   * where body { overflow: hidden } clipped it. The inner flex chain
   * (AIPaneContainer → Tabs → AnalysisReport) then sized its bounded
   * height against that too-tall value, so a long condition list filled
   * the oversized box, produced NO scrollbar, and its bottom rows were
   * silently clipped off-screen. Short briefs fit inside the visible
   * area so the clip went unnoticed until a matter with many claimed
   * conditions made the cut-off (and the missing scrollbar) obvious.
   * height:100% makes the chain compute against the actually-visible
   * height so overflow-y: auto inside AnalysisReport produces a real,
   * reachable scrollbar.
   */
  height: 100%;
  max-height: 100%;
  background: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  margin-left: 8px;
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden; /* Prevent horizontal overflow */
  overflow-y: hidden; /* Prevent vertical overflow */
  box-sizing: border-box;
  flex-shrink: 0;
  
  /* Ensure the AI panel never exceeds viewport */
  position: relative;
  
  /* Ensure the inner panel root can shrink so inner scroll works */
  > * {
    flex: 1 1 0;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

export const AIPaneResizerButton = styled.button`
  position: absolute;
  top: 0;
  left: -4px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  border: none;
  padding: 0;
  z-index: 12;
  user-select: none;

  &:hover,
  &:focus-visible {
    background: rgba(12, 83, 187, 0.12);
    outline: none;
  }
`;

/**
 * Backwards-compat alias. The ChatPane no longer ships under a dedicated
 * panel wrapper — the AI panel hosts the chat surface as one of three
 * tabs — but other modules and tests still import this name. The alias
 * lets them keep working without a coordinated rename in this PR.
 */
export const ChatPaneWrapper = AIPaneWrapper;

export const SideBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 12px 14px;
  height: calc(100vh - 20px);
  width: ${(props) => props.width || "220px"};
  min-width: 200px;
  /* Keep in sync with SIDEBAR_MAX_WIDTH / HOVER_EXPAND_CAP in
     MatterDetails.js. Any value here below those caps silently clips
     both manual-drag and hover-expand widths. */
  max-width: 500px;

  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-right: 8px;
  position: relative;
  transition: ${(props) => (props.$animate === false ? "none" : "width 180ms ease")};

  /* Narrow-viewport handling is driven by the JS auto-adjust effect in
     MatterDetails.js (it clamps the width based on available space when
     the chat panel opens). Keeping a hardcoded media query here would
     overpower the prop-driven width — including the hover-expand state. */
`;

export const BackButton = styled.div`
  display: flex;
  font-family: 'Manrope', sans-serif;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 28px;
  letter-spacing: 0%;
  vertical-align: middle;
  padding: 6px;
  align-items: center;
  color: #5a6c87;
  cursor: pointer;
  &:hover {
    background: #f3f4f7;
  }
`;

export const CollapseBtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
export const ResizerButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  border: none;
  padding: 0;
  z-index: 10;
  user-select: none;

  &:focus {
    outline: none;
  }
`;

export const SideBarContainer = styled.div`
  display: flex;
  height: calc(100vh - 20px);
  position: relative;
  flex-shrink: 0; /* Prevent sidebar container from shrinking */
  max-width: 508px; /* Max sidebar width (500px) + margin (8px) */
`;

// Full-bleed overlay that mounts the shell skeleton ON TOP of the real
// chrome during `matterDetailsStatus === "idle" | "loading"`. Rendered as
// an absolute layer rather than an early-return replacement so that
// `TabView` (a child of the real chrome) stays mounted and can fire its
// `fetchMatterDetail` bootstrap useEffect — otherwise the matter never
// loads and the skeleton shows forever. See the big block comment in
// `MatterDetails.js` next to `isInitialLoad` for the full history.
//
// `z-index: 10` covers the tab strip / resizer / popover but stays under
// the chat pane overlay (which doesn't use z-index explicitly, so 10 is
// enough). `pointer-events: auto` blocks clicks from reaching the hidden
// chrome behind it — otherwise users could accidentally interact with
// half-initialised TabView state during the loading window.
export const ShellSkeletonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background: transparent; /* the skeleton's own wrapper paints the page surface */
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease-out;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;